import newsArticleCover from '../assets/ProjectAssets/NewsArticleAnalysisAssets/CoverImage.png'
import newsArticleArchitectureDiagram from '../assets/ProjectAssets/NewsArticleAnalysisAssets/System_Architecture_Diagram.png'
import newsArticleResultsTable from '../assets/ProjectAssets/NewsArticleAnalysisAssets/Results_Table.png'
import newsArticleReportPdf from '../../src/assets/ProjectAssets/NewsArticleAnalysisAssets/Report.pdf'
import articleLoginPage from '../assets/ProjectAssets/ArticleSharingPlatformAssets/Images/LoginPage.png'
import articleCreate1 from '../assets/ProjectAssets/ArticleSharingPlatformAssets/Images/CreateArticlePage_WithData_1.png'
import articleCreate2 from '../assets/ProjectAssets/ArticleSharingPlatformAssets/Images/CreateArticlePage_WithData_2.png'
import articleDraftLoaded from '../assets/ProjectAssets/ArticleSharingPlatformAssets/Images/CreateArticlePage_DraftLoaded.png'
import articleSearch from '../assets/ProjectAssets/ArticleSharingPlatformAssets/Images/SearchBar_FromHomePage.png'
import articleIndividualChris from '../assets/ProjectAssets/ArticleSharingPlatformAssets/Individual_Submissions/Submission_3/S3_ChrisTurner.pdf'
import articleArchitectureDiagram from '../assets/ProjectAssets/ArticleSharingPlatformAssets/Images/Entity_Architecture.png'
import articleNavBarImage from '../assets/ProjectAssets/ArticleSharingPlatformAssets/Images/Navigation_Bar.png'
import bugReportF1 from '../assets/ProjectAssets/BugReportClassifierAssets/F1ScoreTable.png'
import bugReportReportPdf from '../../src/assets/ProjectAssets/BugReportClassifierAssets/Report.pdf'

const projects = [
  {
    title: 'News Article Bias & Reliability Analysis Platform',
    subtitle: 'Comparative, feature-driven news analysis',
    description:
      'Built a comparative news analysis system that retrieves related coverage, clusters articles by event using semantic embeddings, and computes bias and reliability scores from linguistic, structural and comparative features.',
    slug: 'news-article-analysis',
    featured: true,
    visible: true,
    image: newsArticleCover,
    skills: ['Python', 'Angular', 'NLP', 'Machine learning', 'Semantic Similarity', 'Information Retrieval', 'Feature Engineering', 'Data Processing'],
    organisation: 'University of Birmingham',
    role: 'Final Year Project',
    timeline: 'Sep 2025 - April 2026',
    problem_overview: 'News organisations can report on the same event using substantially different language, framing and levels of attribution. This project investigates whether NLP can be used to compare these differences and provide users with meaningful indicators of potential reliability and bias.',
    implementation_bullets: [
        'Extracts and preprocesses a user-provided news article.',
        'Generates search queries using named entities and TF-IDF to retrieve related reporting.',
        'Uses Sentence Transformers and cosine similarity to compare articles semantically.',
        'Applies agglomerative clustering to group articles covering the same story.',
        'Combines semantic, linguistic and sentiment features to produce comparative reliability and bias indicators.',
        'Presents the analysis through an Angular web interface.'
    ],
    technical_details_table: [
        ['Article Extraction', 'Trafilatura + Newspaper3k'],
        ['NLP', 'spaCy + named-entity recognition'],
        ['Retrieval', 'NewsAPI + RSS Feeds'],
        ['Semantic analysis', 'Sentence Transformers + cosine similarity'],
        ['Clustering', 'Agglomerative hierarchical clustering'],
        ['Reliability Features', 'Attribution density, named entity diversity, hedge ratio, numerical specificity, semantic similarity to other sources (as multiplier)'],
        ['Bias Features', 'Semantic deviation, sentiment, loaded language, headline sensationalism'],
        ['Application', 'Python, Angular, FastAPI']
    ],
    results_bullets: [
        'Successfully developed an end-to-end pipeline for comparing articles covering the same story.',
        'Found that article length and preprocessing can significantly affect semantic similarity, highlighting the importance of data quality in NLP systems.',
        'Combining multiple linguistic and semantic features provided more informative results than relying on similarity alone.',
        'Designed the system as a comparative analytical tool rather than an automated fact checker, making its limitations explicit.'
    ],
    evidence_image: newsArticleCover,
    architecture_diagram: newsArticleArchitectureDiagram,
    results_table_image: newsArticleResultsTable,
    contribution_summary: 'Designed and implemented the complete system, including the article extraction pipeline, NLP-based query generation, semantic representation, article clustering, reliability and bias feature engineering, scoring methodology and Angular frontend.',
    tech_bullets: [
        'Python', 'Angular', 'spaCy', 'scikit-learn', 'Sentence Transformers', 'pandas', 'NewsAPI', 'RSS Feeds'
    ],
    report_pdf: newsArticleReportPdf,
    demo_address: '',
    github_repository_address: '',
  },
  {
    title: 'Article Sharing Platform',
    subtitle: 'Full-stack platform for creating and sharing articles',
    description:
      'A full-stack university news platform developed collaboratively by a team of eight. The application allows users to create, discover and interact with student-written articles, with features including personalised recommendations, search, bookmarks, comments, profiles and AI-generated article summaries.',
    slug: 'article-sharing-platform',
    featured: false,
    visible: true,
    image: articleLoginPage,
    skills: ['Full-Stack Development', 'Java', 'Angular', 'PostgreSQL', 'Spring Boot', 'Docker', 'CI/CD', 'JHipster'],
    organisation: 'University of Birmingham',
    role: 'Team Project',
    timeline: 'Jan 2024 - May 2025',
    problem_overview: 'We were tasked with developing a complete web application as a team, moving from initial wireframes and database modelling through to a publicly deployed application. \n The challenge was to divide development into independent features while ensuring that the resulting frontend, backend and database components integrated into a single application.',
    implementation_introduction: 'UoB News is a student-focused news platform featuring:',
    implementation_bullets: [
        'Article creation and reading',
        'Search and filtering',
        'User profiles and personalised interests',
        'Bookmarks and comments',
        'Engagement leaderboards',
        'AI-generated article summaries',
        'Accessibility and display settings',
        'Public deployment with CI/CD'
    ],
    implementation_paragraph: 'The final application uses persistent user-specific state and integrates the different team members\' features into one system.',
    technical_approach: 'The project followed a vertical-slicing approach, with each team member developing a frontend/backend feature alongside its associated database entity.',
    architecture_paragraph: 'The architecture of my entity is displayed above, alongside the navigation and search bar for our application. This involved the construction of a jdl entity that was used to generate the JHipster application template. The application was developed using JHipster, with Docker and a CI/CD pipeline used to support deployment. The team maintained separate development and production environments.',
    contribution_introduction: 'I was responsible for the Create Article feature. I developed the frontend and backend functionality for creating articles, including:',
    contribution_bullets: [
        'Article creation form and UI',
        'Input validation and error handling',
        'Article/image constraints',
        'Authentication and ownership rules',
        'Prevention of duplicate submissions',
        'Article database integration',
        'Redirecting users to the newly created article',
        'Custom tag creation/allocation',
        'Draft creation and loading',
    ],
    contribution_paragraph: 'I also contributed to deployment, VM setup and populating the application with real data, as well as aiding team members in the creation/integration of their entities in the overall project. My contribution report records the Create Post frontend/backend work alongside these deployment and infrastructure tasks. A particularly important design decision was not allowing authors to edit published articles, preserving the context of existing likes and interactions.',
    learned_knowledge: ['Full-stack development', 'Database-backed applications', 'Team Git workflows', 'CI/CD', 'Docker', 'Agile development', 'Backend validation', 'Deploying web applications'],
    evidence_images: [articleCreate1, articleCreate2, articleDraftLoaded],
    architecture_diagram: [articleArchitectureDiagram, articleNavBarImage],
    results_table_image: '',
    tech_bullets: ['Angular', 'TypeScript', 'JHipster', 'Spring Boot', 'PostgreSQL', 'Git'],
    report_address: '',
    demo_address: '',
    github_repository_address: '',
    additional_media: [articleSearch, articleIndividualChris],
  },
  {
    title: 'Performance Bug Report Classification System',
    subtitle: 'NLP pipeline and statistical evaluation',
    description:
      'An NLP-based machine learning experiment which involved developing a system, for automatically classifying software bug reports as performance-related or non-performance-related, that was better than the Naive Bayes baseline given.',
    slug: 'bug-report-classifier',
    featured: true,
    visible: true,
    image: bugReportF1,
    skills: ['Python', 'Machine learning', 'Classification', 'Model Evaluation', 'scikit-learn', 'TF-IDF', 'Data analysis', 'Statistical Analysis'],
    organisation: 'University of Birmingham',
    role: 'Intelligent Software Engineering Project',
    timeline: 'Jan 2026 - April 2026',
    problem_overview: 'Large software projects can generate thousands of bug reports, making manual classification time-consuming and difficult to scale. This project investigated whether machine learning could automatically identify performance-related bug reports from their textual content and which models were most effective. The coursework provided a Naive Bayes + TF-IDF baseline, which I improved by investigating alternative classification models and evaluating their performance across multiple real-world bug-report datasets.',
    implementation_bullets: [
      'Preprocessed bug-report titles and descriptions and converted them into TF-IDF feature representations.',
      'Investigated different TF-IDF configurations, including n-gram ranges and feature limits.',
      'Implemented Logistic Regression with class weighting to address class imbalance.',
      'Developed an ensemble Voting Classifier combining Logistic Regression, SVM and Random Forest.',
      'Evaluated the models separately across PyTorch, TensorFlow, Caffe, Incubator-MXNet and Keras datasets.',
      'Repeated experiments using different train/test splits and used statistical testing to determine whether improvements were significant.'
    ],
    technical_details_table: [['Language', 'Python'],
        ['Data processing', 'pandas, NumPy'],
        ['Text representation', 'TF-IDF'],
        ['Baseline', 'Naive Bayes'],
        ['Primary model', 'Logistic Regression'],
        ['Ensemble', 'Voting Classifier'],
        ['Ensemble models','Logistic Regression', 'SVM', 'Random Forest'],
        ['Optimisation',	'GridSearchCV'],
        ['Evaluation',	'Accuracy, Precision, Recall, F1'],
        ['Statistical analysis',	'Paired t-test using SciPy']],
    technical_details_paragraph: 'The final Logistic Regression configuration used class weighting to compensate for the imbalance between performance-related and non-performance bug reports. Model selection was aligned with F1-macro, which gives equal importance to both classes.',
    results_summary: 'Logistic Regression consistently outperformed the Naive Bayes baseline in terms of F1-score across all five datasets.',
    results_table_data: [['PyTorch',	'0.6286',	'0.7007'],
        ['TensorFlow',	'0.6254',	'0.8078'],
        ['Caffe',	'0.5416',	'0.6167'],
        ['Incubator-MXNet',	'0.7007',	'0.7528'],
        ['Keras',	'0.6743',	'0.8086']],
    results_description: 'The largest improvement occurred on the TensorFlow dataset, where F1-score increased from 0.6254 to 0.8078. \n The Voting Classifier achieved higher accuracy and precision on most datasets, but its reduced recall resulted in lower F1-scores than Logistic Regression.',
    key_takeaways: [
        'Model choice mattered more than increasing TF-IDF complexity — changing n-gram ranges and feature limits had relatively little effect compared with changing the classifier.',
        'Logistic Regression provided the best overall balance between precision and recall across the datasets.',
        'The improvement over Naive Bayes was statistically significant across all five datasets, with every paired t-test producing a p-value below 0.05.',
        'The project demonstrated that a more complex ensemble does not necessarily produce better results when the underlying representation and dataset characteristics favour a simpler linear model.'
    ],
    key_results_text: 'Logistic Regression achieved a consistent and statistically significant improvement over the baseline while remaining considerably simpler than the ensemble approach.',
    evidence_image: bugReportF1,
    architecture_diagram: '',
    results_table_image: '',
    technical_details: 'The implementation utilizes Python for backend processing, scikit-learn for machine learning, and includes comprehensive statistical evaluation methods.',
    contribution_summary: 'I designed and implemented the improved classification pipeline, including the TF-IDF experimentation, Logistic Regression model, class-imbalance handling, Voting Classifier, hyperparameter optimisation and experimental evaluation. \n I also designed the repeated evaluation process and statistical analysis used to determine whether the observed improvement over the baseline was significant.',
    tech_bullets: ['Python', 'scikit-learn', 'pandas', 'NumPy', 'SciPy', 'TF-IDF', 'Logistic Regression', 'SVM', 'Random Forest'],
    report_pdf: bugReportReportPdf,
    demo_address: '',
    github_repository_address: '',
  },
  {
    title: 'Concurrent Record Database Application',
    subtitle: 'Client-server Java application supporting concurrent access',
    description:
      'Developed a client-server Java application with JavaFX, JDBC and PostgreSQL to support concurrent record access and demonstrate transaction-aware database integration.',
    slug: 'concurrent-record-database',
    featured: false,
    visible: false,
    skills: ['Java', 'JavaFX', 'JDBC', 'PostgreSQL', 'Multithreading'],
    organisation: 'University of Birmingham',
    role: 'Full-Stack Application Development Project',
    timeline: 'March 2024 - April 2024',
    problem_overview: 'The project addresses the challenge of creating a robust database application that supports concurrent access and ensures data integrity through transaction management.',
    implementation_bullets: [
    'The application was developed using Java for the backend, JavaFX for the frontend, JDBC for database connectivity, and PostgreSQL for data storage.'
    ],
    technical_details_table: [],
    results_bullets: [],
    evidence_image: '',
    architecture_diagram: '',
    results_table_image: '',
    technical_details: 'The system incorporates modern Java development practices, including object-oriented programming, multithreading for concurrent access, and efficient database integration.',
    results_summary: 'The application successfully supports concurrent record access and demonstrates robust transaction management, ensuring data consistency and integrity.',
    contribution_summary: 'The project involved designing the system architecture, developing the backend and frontend components, and integrating various technologies to support concurrent database operations.',
    tech_bullets: [],
    report_address: '',
    demo_address: '',
    github_repository_address: '',
  }
]

export default projects

